// Reactive staged-edits store with auto-save through the proxy Worker.
//
// The cube editor calls stageCell() on each edit; the store buffers
// the change in an in-memory record, debounces a 600 ms idle window,
// then POSTs the accumulated edits to /api/drafts/{slug} (saveDraft).
//
// Offline / failure path: edits stay in browser sessionStorage so a
// connection drop does not lose work; when /api/drafts/{slug} succeeds
// again the sessionStorage snapshot is flushed.

import { api, ApiError } from './api';
import { identity } from './identity.svelte';
import type { CubeCellValue, EstablishmentFeeOverride, Jurisdiction, ProductId, SaveDraftResponse, ValueType } from './types';
import {
  applyStagedEditsToCatalogue,
  defaultDraftSlug,
  stagedEditsToPayload,
  stagedEditsStorageKey,
  LEGACY_STAGED_EDITS_STORAGE_KEY,
  type AmountField,
  type FeePath,
  type LoanPurpose,
  type StagedAmountEdit,
  type StagedCellEdit,
  type StagedEstablishmentFeeOverrideEdit,
  type StagedFeeEdit,
  type StagedPurposeEdit,
  type StagedSnapshot,
  type StagedTermsEdit
} from './staged-edits';

const SAVE_DEBOUNCE_MS = 600;

// Tracks whether the legacy non-identity-scoped sessionStorage key has
// already been pruned this page load, so the migration runs at most
// once per browser-tab lifetime.
let legacyKeyPruned = false;

interface SaveStatus {
  state: 'idle' | 'pending' | 'saving' | 'saved' | 'error';
  lastSavedAt: string | null;
  branch: string | null;
  errorMessage: string | null;
}

// lint:edit-category-partial-ok — the cube_edits category is stored as
// `state.edits` for historical reasons (the SPA store predates the
// other *_edits suffixed buckets). The interface and init below
// therefore enumerate five of the six EDIT_CATEGORIES literally; the
// sixth (cube_edits) is the `edits` field. Renaming the field to
// `cube_edits` to satisfy the lint naturally is deferred per
// SPEC-CHANGE-CANDIDATES item 16 option 3 (the EDIT_CATEGORIES
// constant + destructuring refactor across ~20 sites).
const state = $state<{
  jurisdiction: Jurisdiction | null;
  product: ProductId | null;
  draftSlug: string | null;
  // The value type the draft holds. Per spec §1.4, a draft is one
  // change-line of one value type. The store enforces this by clearing
  // the other category buckets whenever a stage* call introduces a new
  // value type. UI surfaces hide affordances for non-matching types
  // when valueType is set.
  valueType: ValueType | null;
  // lint:edit-category-partial-ok — cube_edits is the `edits` field; see file header.
  edits: Record<string, StagedCellEdit>;
  amount_edits: Record<string, StagedAmountEdit>;
  purpose_edits: Record<string, StagedPurposeEdit>;
  fee_edits: Record<string, StagedFeeEdit>;
  fee_override_edits: Record<string, StagedEstablishmentFeeOverrideEdit>;
  // The terms_months category carries the FULL staged list rather than
  // per-term entries because the server contract (term_edits: number[])
  // is a replacement, not a diff. Null when no edit is staged.
  term_edits: StagedTermsEdit | null;
  // Commit SHA the store last observed at the draft branch's tip.
  // Populated by the saveDraft response on every successful save and
  // by adoptDraftFromServer when an existing server-side draft is
  // adopted. Sent on the next saveDraft as expected_commit_sha so the
  // server can refuse a write that would silently merge into drifted
  // state (e.g. another tab moved the branch). Null on a fresh slug
  // — the first save creates the branch and there is nothing to
  // compare against.
  lastObservedCommitSha: string | null;
  save: SaveStatus;
}>({
  // lint:edit-category-partial-ok — cube_edits is `edits` here; see note above.
  jurisdiction: null,
  product: null,
  draftSlug: null,
  valueType: null,
  edits: {},
  amount_edits: {},
  purpose_edits: {},
  fee_edits: {},
  fee_override_edits: {},
  term_edits: null,
  lastObservedCommitSha: null,
  save: { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null }
});

export const stagedEditsState = state;

let debounceHandle: ReturnType<typeof setTimeout> | null = null;

// Save serialisation. Without this, rapid edits that span the
// debounce window each trigger their own flushSave, and the second
// save can start before the first finishes. Each save reads the
// current branch tip, mutates a tree, and PATCHes the ref — two
// concurrent saves both base their commit on the same parent, the
// first wins the ref update, the second hits "not a fast forward"
// (HTTP 422) from GitHub. Symptom Greg hit: removing three staged
// edits in quick succession surfaced
// "Update is not a fast forward" on the second PATCH.
//
// Strategy: a single in-flight save at a time. New flushSave calls
// arriving while one is running set a sticky pending flag; when the
// current save completes the flag triggers exactly one follow-up
// save with whatever the latest state is at that moment. This
// coalesces a burst of edits into at-most-one follow-up flush, so
// rapid removes complete with one or two sequential PATCHes rather
// than a fan-out.
let isSaving = false;
let pendingFlush = false;

function pruneLegacyStorageKey(): void {
  if (legacyKeyPruned) return;
  legacyKeyPruned = true;
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(LEGACY_STAGED_EDITS_STORAGE_KEY)) {
      // The pre-identity-scoping unscoped key let one identity's
      // staged edits rehydrate into another identity's session. Drop
      // it on first sight; the legacy entry is unrecoverable under
      // the new model because it does not carry an identity
      // attribution.
      window.sessionStorage.removeItem(LEGACY_STAGED_EDITS_STORAGE_KEY);
    }
  } catch {
    // sessionStorage access can throw in private-browsing contexts;
    // the prune is best-effort and the new identity-scoped read path
    // does not depend on it.
  }
}

function persistToSession(): void {
  if (typeof window === 'undefined') return;
  const key = stagedEditsStorageKey(identity.email);
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(snapshot()));
  } catch {
    // Ignore quota errors; the proxy save is the authoritative path.
  }
}

function snapshot(): StagedSnapshot | null {
  if (!state.jurisdiction || !state.product || !state.draftSlug) return null;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  return {
    jurisdiction: state.jurisdiction,
    product: state.product,
    draftSlug: state.draftSlug,
    valueType: state.valueType,
    edits: state.edits,
    amount_edits: state.amount_edits as Record<AmountField, StagedAmountEdit>,
    purpose_edits: state.purpose_edits,
    fee_edits: state.fee_edits as Record<FeePath, StagedFeeEdit>,
    fee_override_edits: state.fee_override_edits,
    term_edits: state.term_edits,
    lastObservedCommitSha: state.lastObservedCommitSha
  };
}

export function loadFromSession(jurisdiction: Jurisdiction, product: ProductId): boolean {
  if (typeof window === 'undefined') return false;
  pruneLegacyStorageKey();
  const key = stagedEditsStorageKey(identity.email);
  if (!key) return false;
  const raw = window.sessionStorage.getItem(key);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as StagedSnapshot;
    if (parsed.jurisdiction !== jurisdiction || parsed.product !== product) return false;
    state.jurisdiction = parsed.jurisdiction;
    state.product = parsed.product;
    state.draftSlug = parsed.draftSlug;
    state.valueType = parsed.valueType ?? null;
    // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
    state.edits = { ...parsed.edits };
    state.amount_edits = { ...(parsed.amount_edits as Record<string, StagedAmountEdit>) };
    state.purpose_edits = { ...(parsed.purpose_edits ?? {}) };
    state.fee_edits = { ...((parsed.fee_edits ?? {}) as Record<string, StagedFeeEdit>) };
    state.fee_override_edits = { ...(parsed.fee_override_edits ?? {}) };
    state.term_edits = parsed.term_edits ?? null;
    state.lastObservedCommitSha = parsed.lastObservedCommitSha ?? null;
    return true;
  } catch {
    return false;
  }
}

function hasAnyStagedEdits(): boolean {
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  return Object.keys(state.edits).length > 0
    || Object.keys(state.amount_edits).length > 0
    || Object.keys(state.purpose_edits).length > 0
    || Object.keys(state.fee_edits).length > 0
    || Object.keys(state.fee_override_edits).length > 0
    || state.term_edits !== null;
}

export function beginEditing(jurisdiction: Jurisdiction, product: ProductId, draftSlug?: string): void {
  if (state.jurisdiction === jurisdiction && state.product === product && hasAnyStagedEdits()) {
    return; // resume in-place
  }
  state.jurisdiction = jurisdiction;
  state.product = product;
  state.draftSlug = draftSlug ?? defaultDraftSlug(jurisdiction, product);
  state.valueType = null;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  state.edits = {};
  state.amount_edits = {};
  state.purpose_edits = {};
  state.fee_edits = {};
  state.fee_override_edits = {};
  state.term_edits = null;
  state.lastObservedCommitSha = null;
  state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };
  persistToSession();
}

// Hydrate the staged-edits store from a server-side draft. Used by the
// re-author affordance (INTERFACE-SPECIFICATION.md §8.1): the server
// has just created a draft whose cube file already encodes the staged
// edits via the saveDraft commit, but the SPA's local store knows
// nothing about it. Without this hydration the editor opens empty,
// hides the seeded changes, and any new edit creates a different
// default-slug draft.
//
// The function fetches the draft via api.loadDraft, looks up each cube
// cell's before value from the catalogue (main's cube), reconstructs
// the StagedCellEdit shapes, and persists the result to sessionStorage
// so CatalogueDetail.svelte's onMount loadFromSession recovers the
// hydrated state cleanly.
//
// Returns the loaded draft summary so the caller can navigate to the
// right /catalogue/detail/{j}/{p} route without re-fetching.
export async function adoptDraftFromServer(
  draft_slug: string,
  catalogue: import('./types').ProductCatalogueFile
): Promise<{ jurisdiction: Jurisdiction; product: ProductId; staged_change_count: number; value_type: ValueType | null }> {
  const loaded = await api.loadDraft(draft_slug);

  state.jurisdiction = loaded.jurisdiction;
  state.product = loaded.product;
  state.draftSlug = draft_slug;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  state.edits = {};
  state.amount_edits = {};
  state.purpose_edits = {};
  state.fee_edits = {};
  state.fee_override_edits = {};
  state.term_edits = null;
  // Remember the branch tip SHA we just saw; the next saveDraft will
  // pass it as expected_commit_sha so the server can refuse a write
  // that would clobber drift from another tab or out-of-band edits.
  state.lastObservedCommitSha = loaded.commit_sha;
  state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };

  let derivedValueType: ValueType | null = null;

  // Cube cell edits. The before value comes from the catalogue (main's
  // cube) at the same coordinate; the staged value is the recovered
  // new_value. We look up by walking the rate_cube tree because the
  // pricing model differs between bureau-band and grade-subgrade.
  for (const edit of loaded.recovered_edits.cube_edits) {
    const parts = edit.coordinates.split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean);
    let node: unknown = catalogue.rate_cube;
    let resolved = true;
    for (const segment of parts) {
      if (node == null || typeof node !== 'object' || !(segment in (node as Record<string, unknown>))) {
        resolved = false;
        break;
      }
      node = (node as Record<string, unknown>)[segment];
    }
    const before = (resolved && node && typeof node === 'object'
      ? (node as import('./types').CubeCellValue)
      : { interest_rate_pa: null, max_loan_amount: null });
    const staged: Partial<import('./types').CubeCellValue> = {
      ...(edit.new_value as Partial<import('./types').CubeCellValue>)
    };
    state.edits[edit.coordinates] = {
      coordinates: edit.coordinates,
      before,
      staged
    };
    derivedValueType = 'rates';
  }

  // Loan-amount-range edits.
  for (const edit of loaded.recovered_edits.amount_edits) {
    const before = catalogue.loan_amount_range?.[edit.field] ?? 0;
    state.amount_edits[edit.field] = {
      field: edit.field,
      before,
      staged: edit.new_value
    };
    derivedValueType = 'amounts';
  }

  // Fee edits. The before value is read at the same dotted path on
  // the catalogue's fees object so the StagedFeeEdit carries both
  // sides for the editor's table.
  for (const edit of loaded.recovered_edits.fee_edits) {
    const segments = edit.path.split('.');
    let node: unknown = catalogue;
    let resolved = true;
    for (const segment of segments) {
      if (node == null || typeof node !== 'object' || !(segment in (node as Record<string, unknown>))) {
        resolved = false;
        break;
      }
      node = (node as Record<string, unknown>)[segment];
    }
    state.fee_edits[edit.path] = {
      path: edit.path as import('./staged-edits').FeePath,
      before: resolved ? node : null,
      staged: edit.new_value
    };
    derivedValueType = 'fees';
  }

  // Establishment-fee override edits. The before value is the override
  // currently present on the catalogue's credit-rating-axis-level node
  // (or null when none is present); the staged value is the recovered
  // new_value (null means the user staged a removal).
  for (const edit of loaded.recovered_edits.fee_override_edits || []) {
    const before = (catalogue.rate_cube?.[edit.credit_rating_id] as Record<string, unknown> | undefined)?.establishment_fee_override as EstablishmentFeeOverride | undefined;
    state.fee_override_edits[edit.credit_rating_id] = {
      credit_rating_id: edit.credit_rating_id,
      before: before ?? null,
      staged: edit.new_value as EstablishmentFeeOverride | null
    };
    derivedValueType = 'fees';
  }

  // Terms edits. The server contract is the full replacement list, so a
  // single staged entry carries before (catalogue's current terms_months)
  // and staged (the recovered list).
  if (Array.isArray(loaded.recovered_edits.term_edits) && loaded.recovered_edits.term_edits.length > 0) {
    const baseline = [...(catalogue.terms_months ?? [])].sort((a, b) => a - b);
    const stagedList = [...loaded.recovered_edits.term_edits].sort((a, b) => a - b);
    state.term_edits = { before: baseline, staged: stagedList };
    derivedValueType = 'terms';
  }

  // Purpose edits. add / remove / update each carry the structure the
  // staged-edits store keys on (purpose_id).
  for (const edit of loaded.recovered_edits.purpose_edits) {
    if (edit.action === 'add') {
      const purpose = edit.purpose as import('./staged-edits').LoanPurpose;
      state.purpose_edits[purpose.id] = {
        action: 'add',
        purpose_id: purpose.id,
        staged: purpose
      };
    } else if (edit.action === 'remove') {
      const existing = (catalogue.loan_purposes || []).find((p) => p.id === edit.purpose_id);
      if (existing) {
        state.purpose_edits[edit.purpose_id] = {
          action: 'remove',
          purpose_id: edit.purpose_id,
          before: existing as import('./staged-edits').LoanPurpose
        };
      }
    } else if (edit.action === 'update') {
      const existing = (catalogue.loan_purposes || []).find((p) => p.id === edit.purpose_id);
      state.purpose_edits[edit.purpose_id] = {
        action: 'update',
        purpose_id: edit.purpose_id,
        before: (existing ?? edit.purpose) as import('./staged-edits').LoanPurpose,
        staged: edit.purpose as import('./staged-edits').LoanPurpose
      };
    }
    derivedValueType = 'purposes';
  }

  // Value type may have been picked up from multiple categories — only
  // one bucket is non-empty at a time per spec §1.4 (the saveDraft
  // commit already enforced this). Settle on whichever derivedValueType
  // got assigned last; if no edits at all (zero-intent re-author would
  // have errored server-side), leave null.
  state.valueType = derivedValueType;

  persistToSession();

  return {
    jurisdiction: loaded.jurisdiction,
    product: loaded.product,
    staged_change_count: loaded.staged_change_count,
    value_type: derivedValueType
  };
}

export function clearEditing(): void {
  state.valueType = null;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  state.edits = {};
  state.amount_edits = {};
  state.purpose_edits = {};
  state.fee_edits = {};
  state.fee_override_edits = {};
  state.term_edits = null;
  state.lastObservedCommitSha = null;
  state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };
  persistToSession();
}

// Edit-mutation discipline: reassign state.edits to a NEW object on every
// stage / unstage rather than mutating the existing one in place. Svelte 5's
// $state proxy tracks property-level reads on plain objects, but downstream
// $derived expressions that iterate via Object.values / Object.entries do
// not always invalidate cleanly when an existing key is overwritten or a
// new key is added in place. Top-level reassignment guarantees every
// dependent re-evaluates. The performance cost (one fresh spread per
// keystroke-quantum edit) is negligible relative to the auto-save round-
// trip.
// Thrown when a stage* call would mix edits from two value types in
// one draft. Spec §1.4 says a draft is one change-line of one value
// type; the store enforces this. The UI catches and surfaces a banner
// asking the operator to submit or discard the in-flight edits before
// switching value types.
export class ValueTypeMismatchError extends Error {
  constructor(public current: ValueType, public attempted: ValueType) {
    super(`Cannot stage ${attempted} edits while ${current} edits are in progress. Submit or discard the ${current} edits first.`);
    this.name = 'ValueTypeMismatchError';
  }
}

function ensureValueType(next: ValueType): void {
  if (state.valueType === null || state.valueType === next) {
    state.valueType = next;
    return;
  }
  throw new ValueTypeMismatchError(state.valueType, next);
}

export function stageCell(coordinates: string, before: CubeCellValue, field: 'interest_rate_pa' | 'max_loan_amount', value: number): void {
  ensureValueType('rates');
  const previous = state.edits[coordinates];
  const merged: StagedCellEdit = previous
    ? { coordinates, before: previous.before, staged: { ...previous.staged, [field]: value } }
    : { coordinates, before, staged: { [field]: value }, field };
  state.edits = { ...state.edits, [coordinates]: merged };
  persistToSession();
  scheduleSave();
}

export function unstageCell(coordinates: string): void {
  const { [coordinates]: _removed, ...rest } = state.edits;
  state.edits = rest;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

export function stageAmountEdit(field: AmountField, before: number, value: number): void {
  ensureValueType('amounts');
  const merged: StagedAmountEdit = { field, before, staged: value };
  state.amount_edits = { ...state.amount_edits, [field]: merged };
  persistToSession();
  scheduleSave();
}

export function unstageAmountEdit(field: AmountField): void {
  const { [field]: _removed, ...rest } = state.amount_edits;
  state.amount_edits = rest;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

// Stage a purpose-edit. Update is the default action for an in-list
// purpose; add and remove are the alternatives. Collapses repeated
// stage calls on the same purpose_id so the store carries the
// operator's most recent intent for each id at most once.
//
// - Repeated 'update' on the same id: keep the original before and
//   replace staged with the latest. Reverting back to before unstages.
// - 'add' then 'update' on the same id: stays an 'add' with the merged
//   purpose so the proposal contains a single add (not add + update).
// - 'add' then 'remove': cancels both — the net intent is no change.
// - 'remove' then 'add' (rare; the operator would have to re-add an
//   id that had just been removed): becomes an update if the id was
//   originally present in the catalogue.
export function stageUpdatePurpose(purposeId: string, before: LoanPurpose, staged: LoanPurpose): void {
  ensureValueType('purposes');
  if (isDeepEqualPurpose(before, staged)) {
    unstagePurposeEdit(purposeId);
    return;
  }
  const existing = state.purpose_edits[purposeId];
  let merged: StagedPurposeEdit;
  if (existing?.action === 'add') {
    merged = { action: 'add', purpose_id: purposeId, staged };
  } else {
    merged = { action: 'update', purpose_id: purposeId, before, staged };
  }
  state.purpose_edits = { ...state.purpose_edits, [purposeId]: merged };
  persistToSession();
  scheduleSave();
}

export function stageRemovePurpose(purposeId: string, before: LoanPurpose): void {
  ensureValueType('purposes');
  const existing = state.purpose_edits[purposeId];
  if (existing?.action === 'add') {
    // 'add' then 'remove' on the same id cancels both.
    unstagePurposeEdit(purposeId);
    return;
  }
  const merged: StagedPurposeEdit = { action: 'remove', purpose_id: purposeId, before };
  state.purpose_edits = { ...state.purpose_edits, [purposeId]: merged };
  persistToSession();
  scheduleSave();
}

export function stageAddPurpose(purpose: LoanPurpose): void {
  ensureValueType('purposes');
  const merged: StagedPurposeEdit = { action: 'add', purpose_id: purpose.id, staged: purpose };
  state.purpose_edits = { ...state.purpose_edits, [purpose.id]: merged };
  persistToSession();
  scheduleSave();
}

export function unstagePurposeEdit(purposeId: string): void {
  const { [purposeId]: _removed, ...rest } = state.purpose_edits;
  state.purpose_edits = rest;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

function isDeepEqualPurpose(a: LoanPurpose, b: LoanPurpose): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Stage an edit on a top-level fees path. Reverting back to the
// baseline unstages. The before value is captured the first time a
// path is staged so subsequent edits do not lose the original; if
// the user reverts back to the original the entry is dropped.
export function stageFeeEdit(path: FeePath, before: unknown, staged: unknown): void {
  ensureValueType('fees');
  if (JSON.stringify(before) === JSON.stringify(staged)) {
    unstageFeeEdit(path);
    return;
  }
  const existing = state.fee_edits[path];
  const merged: StagedFeeEdit = {
    path,
    before: existing ? existing.before : before,
    staged
  };
  state.fee_edits = { ...state.fee_edits, [path]: merged };
  persistToSession();
  scheduleSave();
}

export function unstageFeeEdit(path: FeePath): void {
  const { [path]: _removed, ...rest } = state.fee_edits;
  state.fee_edits = rest;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

// Per-credit-rating establishment-fee override. `staged: null` means the
// user wants to remove the override from the credit-rating-axis-level node;
// a populated `staged` either sets the override (when `before` is null)
// or replaces the existing override. A no-op edit (staged == before)
// drops the staged entry rather than recording an empty edit.
export function stageFeeOverrideEdit(
  creditRatingId: string,
  before: EstablishmentFeeOverride | null,
  staged: EstablishmentFeeOverride | null
): void {
  ensureValueType('fees');
  if (JSON.stringify(before) === JSON.stringify(staged)) {
    unstageFeeOverrideEdit(creditRatingId);
    return;
  }
  const existing = state.fee_override_edits[creditRatingId];
  const merged: StagedEstablishmentFeeOverrideEdit = {
    credit_rating_id: creditRatingId,
    before: existing ? existing.before : before,
    staged
  };
  state.fee_override_edits = { ...state.fee_override_edits, [creditRatingId]: merged };
  persistToSession();
  scheduleSave();
}

export function unstageFeeOverrideEdit(creditRatingId: string): void {
  const { [creditRatingId]: _removed, ...rest } = state.fee_override_edits;
  state.fee_override_edits = rest;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

// Stage a terms_months replacement. The server contract carries the FULL
// list (term_edits: number[]), not a diff, so the store keeps a single
// entry with before / staged rather than per-term records. A no-op edit
// (staged sorted equals before sorted) drops the entry so the operator
// can revert by removing additions or re-adding removals to the baseline
// without leaving a residual empty edit.
function termListsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const aa = [...a].sort((x, y) => x - y);
  const bb = [...b].sort((x, y) => x - y);
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false;
  }
  return true;
}

export function stageTermEdits(before: number[], staged: number[]): void {
  ensureValueType('terms');
  if (termListsEqual(before, staged)) {
    unstageTermEdits();
    return;
  }
  const baseline = state.term_edits ? state.term_edits.before : [...before].sort((a, b) => a - b);
  state.term_edits = {
    before: baseline,
    staged: [...staged].sort((a, b) => a - b)
  };
  persistToSession();
  scheduleSave();
}

export function unstageTermEdits(): void {
  state.term_edits = null;
  if (!hasAnyStagedEdits()) {
    state.valueType = null;
  }
  persistToSession();
  scheduleSave();
}

export function discardStaged(): void {
  state.valueType = null;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  state.edits = {};
  state.amount_edits = {};
  state.purpose_edits = {};
  state.fee_edits = {};
  state.fee_override_edits = {};
  state.term_edits = null;
  state.lastObservedCommitSha = null;
  state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };
  persistToSession();
}

// Reset the entire in-memory store. Called on identity change so the
// previous identity's edits and save-error state do not bleed through
// into the new identity's session. sessionStorage under the previous
// identity's key is left intact so re-selecting that identity later
// resumes its draft cleanly.
export function resetForIdentity(): void {
  if (debounceHandle) {
    clearTimeout(debounceHandle);
    debounceHandle = null;
  }
  isSaving = false;
  pendingFlush = false;
  state.jurisdiction = null;
  state.product = null;
  state.draftSlug = null;
  state.valueType = null;
  // lint:edit-category-partial-ok — cube_edits is `state.edits` (see file header note above the state declaration).
  state.edits = {};
  state.amount_edits = {};
  state.purpose_edits = {};
  state.fee_edits = {};
  state.fee_override_edits = {};
  state.term_edits = null;
  state.lastObservedCommitSha = null;
  state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };
}

function scheduleSave(): void {
  if (debounceHandle) clearTimeout(debounceHandle);
  state.save.state = 'pending';
  debounceHandle = setTimeout(() => {
    void flushSave();
  }, SAVE_DEBOUNCE_MS);
}

export async function flushSave(): Promise<void> {
  if (isSaving) {
    pendingFlush = true;
    return;
  }
  isSaving = true;
  try {
    await performSave();
  } finally {
    isSaving = false;
    if (pendingFlush) {
      pendingFlush = false;
      void flushSave();
    }
  }
}

async function performSave(): Promise<void> {
  if (!hasAnyStagedEdits()) {
    state.save = { state: 'idle', lastSavedAt: null, branch: null, errorMessage: null };
    return;
  }
  const snap = snapshot();
  if (!snap) return;
  state.save.state = 'saving';
  state.save.errorMessage = null;
  try {
    const payload = stagedEditsToPayload(snap);
    // Include the last commit SHA we observed at the branch tip (from
    // the previous saveDraft response or from adoptDraftFromServer) so
    // the server can reject a write into drifted state. Omitted on
    // the first save of a fresh slug (lastObservedCommitSha is null),
    // where the branch does not yet exist and the server has nothing
    // to compare against.
    if (state.lastObservedCommitSha) {
      payload.expected_commit_sha = state.lastObservedCommitSha;
    }
    const response: SaveDraftResponse = await api.saveDraft(snap.draftSlug, payload);
    state.lastObservedCommitSha = response.commit_sha;
    state.save = {
      state: 'saved',
      lastSavedAt: response.saved_at,
      branch: response.branch,
      errorMessage: null
    };
    persistToSession();
  } catch (err) {
    state.save.state = 'error';
    state.save.errorMessage = err instanceof ApiError
      ? `${err.code}: ${err.message}`
      : err instanceof Error
      ? err.message
      : 'save_failed';
  }
}

export function applyStagedToCurrent(currentCube: import('./types').ProductCatalogueFile | null): import('./types').ProductCatalogueFile | null {
  if (!currentCube) return null;
  if (Object.keys(state.edits).length === 0) return currentCube;
  return applyStagedEditsToCatalogue(currentCube, state.edits);
}

// Test hooks — not used in production code paths.
export const __testing = { SAVE_DEBOUNCE_MS };
